// eslint-disable-next-line no-unused-vars
import React, { memo, useCallback, useEffect, useState } from 'react';
import { object, bool, func, number } from 'prop-types';
import useSWR, { useSWRConfig } from 'swr';
import { useContextSelector } from 'use-context-selector';
import debounce from 'lodash/debounce';
import { useTranslation } from 'react-i18next';

import OrderInfoComponent from 'Components/Order/OrderInfo';
import {
  API_ORDER_OPTIONS,
  API_STATIC_BLOCKS,
  API_CHECKOUT_ADDRESSES,
  API_COUNTIES,
  API_COUNTRIES,
  API_LOCKERS,
  COUNTRIES,
  EMAIL_REGEX
} from 'Constants';
import { UserContext } from 'Contexts/UserContext';
import useFetch from 'Hooks/useFetch';

const initialFormValidation = {
  deliveryAddress: {
    isTouched: false,
    isValid: false,
  },
  deliveryOption: {
    isSelected: false,
  },
  billingAddress: {
    isTouched: false,
    isValid: false,
  },
  billingDetails: {
    isTouched: false,
    isValid: false,
    error: ''
  },
  paymentOption: {
    isSelected: false,
  }
};

const initialDsFormValidation = {
  deliveryType: {
    isSelected: false,
  },
  deliveryMode: {
    isSelected: false,
  },
  name: {
    isTouched: false,
    isValid: false,
    error: ''
  },
  country: {
    isTouched: false,
    isValid: false,
  },
  county: {
    isTouched: false,
    isValid: false,
  },
  city: {
    isTouched: false,
    isValid: false,
  },
  locker: {
    isTouched: false,
    isValid: false,
  },
  address: {
    isTouched: false,
    isValid: false,
    error: ''
  },
  phone: {
    isTouched: false,
    isValid: false,
    error: ''
  },
  recipientEmail: {
    isTouched: false,
    isValid: false,
    error: ''
  },
  postalCode: {
    isTouched: false,
    isValid: false,
    error: ''
  },
  internalRefCode: {
    isTouched: false,
    isValid: false,
    error: ''
  },
  deliveryLockerCode: {
    isTouched: false,
    isValid: false,
    error: ''
  },
  deliveryOption: {
    isSelected: false,
  },
  billingAddress: {
    isTouched: false,
    isValid: false,
  },
  billingDetails: {
    isTouched: false,
    isValid: false,
    error: ''
  },
  paymentOption: {
    isSelected: false,
  }
};

const mappedDeliveryTypes = {
  internalCourier: 1,
  emktp: 2,
  externalCourier: 3,
  trendyol: 4
};

const COUNTIES = 'counties';

const OrderInfo = memo(({ order, editable = true, selectedDSCountry, setSelectedDSCountry, setIsFormValid, shouldShowErrors }) => {
  const { cache } = useSWRConfig();
  const fetch = useFetch();
  const {
    email,
    loggedIn,
    userPaymentOption,
    userTransportOption,
    canPlaceEmagOrder,
    canPlaceExternalOrder,
    canPlaceInternalOrder,
    emagDSCountries,
    hasInternalDropShipmentSamedayLockerTransport,
    hasExternalDropShipmentSamedayLockerTransport,
  } = useContextSelector(UserContext, ({ data }) => ({
    email: data?.email,
    loggedIn: data?.loggedIn,
    canPlaceEmagOrder: !!data?.canPlaceEmagMarketplaceDropShipmentOrder,
    canPlaceExternalOrder: !!data?.canPlaceExternalDropShipmentOrder,
    canPlaceInternalOrder: !!data?.canPlaceInternalDropShipmentOrder,
    emagDSCountries: data?.emagMarketplaceDropShipmentCountries,
    hasInternalDropShipmentSamedayLockerTransport: data?.hasInternalDropShipmentSamedayLockerTransport,
    hasExternalDropShipmentSamedayLockerTransport: data?.hasExternalDropShipmentSamedayLockerTransport,
  }));
  const { t } = useTranslation();

  const [shippingAddresses, setShippingAddresses] = useState([]);
  const [billingAddresses, setBillingAddresses] = useState([]);
  const [selectedBillingAddress, setSelectedBillingAddress] = useState(null);
  const [selectedShippingAddress, setSelectedShippingAddress] = useState(null);
  const [countries, setCountries] = useState(null);
  const [counties, setCounties] = useState(null);
  const [cities, setCities] = useState(null);
  const [lockers, setLockers] = useState(null);
  const [formValidation, setFormValidation] = useState({ ...initialFormValidation });
  const [dsFormValidation, setDsFormValidation] = useState({ ...initialDsFormValidation });

  const {
    data: addresses,
    isValidating: loadingAddresses,
    error: addressesError
    // mutate: mutateAddresses
  } = useSWR(
    loggedIn && email
      ? `${ API_CHECKOUT_ADDRESSES }?uid=${ email }`
      : null,
    fetch,
    {
      revalidateOnMount: true
    }
  );

  const {
    data: orderOptions,
    isValidating: loadingOptions,
    mutate: mutateOptions
  } = useSWR(
    loggedIn && email
      ? `${ API_ORDER_OPTIONS }?drop_shipping=${ order.dropShipment }&uid=${ email }`
      : null,
    fetch,
    {
      revalidateOnMount: true
    }
  );

  const {
    data: staticBlocks,
    isValidating: loadingBlocks
    // mutate: mutateBlocks
  } = useSWR(
    loggedIn && email
      ? `${ API_STATIC_BLOCKS }/transport?drop_shipping=${ order.dropShipment }&uid=${ email }`
      : null,
    fetch,
    {
      revalidateOnMount: true
    }
  );

  useEffect(() => {
    if (
      userPaymentOption
      && !order.paymentOption
      && orderOptions?.paymentOptions?.find(opt => opt.id === userPaymentOption)
    ) {
      order.setPaymentOption(userPaymentOption);
    }
  }, [
    order.paymentOption,
    orderOptions?.paymentOptions,
    userPaymentOption
  ]);

  useEffect(() => {
    if (orderOptions?.transportOptions) {
      if (order.transportOption) {
        const existingOpt = orderOptions.transportOptions.find(opt => opt.id === order.transportOption) || {};
        if (
          existingOpt.code === 'SATURDAY_DELIVERY'
          && 'isAvailable' in existingOpt
          && !existingOpt.isAvailable
        ) {
          order.setTransportOption(null);
        }
      } else if (userTransportOption) {
        const existingOpt = orderOptions.transportOptions.find(opt => opt.id === userTransportOption);
        if (existingOpt) {
          if (existingOpt.code === 'SATURDAY_DELIVERY') {
            if (
              'isAvailable' in existingOpt
              && existingOpt.isAvailable
            ) {
              order.setTransportOption(userTransportOption);
            }
          } else {
            order.setTransportOption(userTransportOption);
          }
        }
      }
    }
  }, [
    order.transportOption,
    orderOptions?.transportOptions,
    userTransportOption
  ]);

  useEffect(() => {
    if (orderOptions?.transportOptions) {
      const date = new Date();
      const day = date.getUTCDay();
      const utcHour = date.getUTCHours();
      const utcMins = date.getUTCMinutes();
      const offset = date.getTimezoneOffset();
      const summerTime = Math.abs(offset / 60) === 2;
      const localRoHour = utcHour + (summerTime ? 3 : 2);
      if (!('isAvailable' in orderOptions.transportOptions.at(0))) {
        mutateOptions({
          ...orderOptions,
          transportOptions: orderOptions.transportOptions.reduce((acc, opt) => {
            const newOpt = {
              ...opt,
              isAvailable: true
            };
            if (newOpt.code === 'SATURDAY_DELIVERY') {
              if (day === 5) {
                newOpt.isAvailable = (
                  localRoHour < 16
                  || (localRoHour === 16 && utcMins <= 0)
                );
              } else {
                newOpt.isAvailable = false;
              }
            }
            acc.push(newOpt);
            return acc;
          }, [])
        }, false);
      }
    }
  }, [orderOptions?.transportOptions]);

  const getCountries = useCallback(
    debounce(fetch, 250, { leading: true, trailing: false }),
    []
  );
  const getCounties = useCallback(
    debounce(fetch, 250, { leading: true, trailing: false }),
    []
  );
  const getCities = useCallback(
    debounce(fetch, 250, { leading: true, trailing: false }),
    []
  );
  const getLockers = useCallback(
    debounce(fetch, 250, { leading: true, trailing: false }),
    []
  );

  useEffect(() => {
    if (
      order?.deliveryAddressData?.deliveryType
      && order?.deliveryAddressData?.deliveryMode
    ) {
      const path = API_COUNTRIES;
      const dropShipmentQuery = `drop_shipment=${ mappedDeliveryTypes[order.deliveryAddressData.deliveryType] }`;
      const transportQuery = `drop_shipment_transport=${ order?.deliveryAddressData?.deliveryMode === 'address' ? '1' : '2' }`;

      setCountries([]);
      getCountries(`${ path }?${ dropShipmentQuery }&${ transportQuery }`).then(data => {
        if (data) {
          setCountries(data.map(i => ({ ...i, label: i.name })));
        }
      });
    }
  },
  [
    order?.deliveryAddressData?.deliveryMode,
    order?.deliveryAddressData?.deliveryType,
  ]);

  useEffect(() => {
    if (!(order.deliveryAddressData?.deliveryType !== 'emktp' && order.deliveryAddressData?.deliveryMode === 'locker')) {
      handleOnSetLocker({ selectedItem: null });
    }
  }, [
    order?.deliveryAddressData?.deliveryType,
    order?.deliveryAddressData?.deliveryMode
  ]);

  useEffect(() => {
    let country;
    const toLocker = order.deliveryAddressData?.deliveryType !== 'emktp' && order.deliveryAddressData?.deliveryMode === 'locker';
    if (order?.deliveryAddressData?.country) {
      country = (countries || []).find(c => c.id === order.deliveryAddressData.country);
      if (!country) return;
      if (setSelectedDSCountry) {
        setSelectedDSCountry(country.id);
      }
      const key = `${ API_COUNTRIES }/${ country.id }/${ COUNTIES }${ toLocker ? '?easybox=1' : '' }`;
      if (!cache.has(key)) {
        setCounties([]);
        getCounties(key).then(data => {
          const mappedCounties = (data || []).map(i => ({ ...i, label: i.name }));
          cache.set(key, mappedCounties);
          setCounties(mappedCounties);
          if (mappedCounties.length === 1) {
            const county = mappedCounties.at(0);
            if (county) {
              handleSetLocation('county')({ selectedItem: { name: county?.label || county?.name } });
            }
          }
        });
      } else {
        const cachedCounties = cache.get(key);
        setCounties(cachedCounties);
        if (cachedCounties.length === 1) {
          const county = cachedCounties.at(0);
          if (county) {
            handleSetLocation('county')({ selectedItem: { name: county?.label || county?.name } });
          }
        }
      }
    }
    if (order?.deliveryAddressData?.county) {
      const county = (counties || []).find(c => c.id === order.deliveryAddressData.county);
      if (!county) return;
      const key = `${ API_COUNTRIES }/${ country.id }/${ COUNTIES }/${ county.erpCountyId }/cities${ toLocker ? '?easybox=1' : '' }`;
      if (!cache.has(key)) {
        setCities([]);
        getCities(key).then(data => {
          const mappedCities = (data || []).map(i => ({ ...i, label: i.name }));
          cache.set(key, mappedCities);
          setCities(mappedCities);
          if (mappedCities.length === 1) {
            const city = mappedCities.at(0);
            if (city) {
              handleSetLocation('city')({ selectedItem: { name: city?.label || city?.name } });
            }
          }
        });
      } else {
        const cachedCities = cache.get(key);
        setCities(cachedCities);
        if (cachedCities.length === 1) {
          const city = cachedCities.at(0);
          if (city) {
            handleSetLocation('city')({ selectedItem: { name: city?.label || city?.name } });
          }
        }
      }
    }
    if (
      order?.deliveryAddressData?.city
      && order?.deliveryAddressData?.deliveryType !== 'emktp'
      && order?.deliveryAddressData?.deliveryMode === 'locker'
    ) {
      const city = (cities || []).find(c => c.name === order.deliveryAddressData.city);
      if (!city) return;
      const key = `${ API_LOCKERS }?urgent_cargus_city_id=${ city?.id }`;
      if (!cache.has(key)) {
        setLockers([]);
        getLockers(key).then(data => {
          const mappedLockers = (data || []).map(i => ({ ...i, label: i.name, value: i.lockerId, zipCode: i.postalCode }));
          cache.set(key, mappedLockers);
          setLockers(mappedLockers);
          if (mappedLockers.length === 1) {
            const locker = mappedLockers.at(0);
            if (locker) {
              handleOnSetLocker({ selectedItem: { name: locker?.label || locker?.name } });
            }
          }
        });
      } else {
        const cachedLockers = cache.get(key);
        setLockers(cachedLockers);
        if (cachedLockers.length === 1) {
          const locker = cachedLockers.at(0);
          if (locker) {
            handleOnSetLocker({ selectedItem: { name: locker?.label || locker?.name } });
          }
        }
      }
    }
  }, [
    order?.deliveryAddressData?.country,
    order?.deliveryAddressData?.county,
    order?.deliveryAddressData?.city,
    countries,
    counties,
    cities
  ]);

  useEffect(() => {
    if (addresses?.length) {
      const { shipping, billing } = addresses.reduce((acc, address) => {
        const parsedAddress = {
          id: address.addressId,
          label: `${ address.street } ${ address.number }, ${ address.county?.name }, ${ address.city?.name }, ${ address.country?.name }`,
          fullAddress: `${ address.street } ${ address.number }, ${ address.county?.name }, ${ address.city?.name } ${ address.country?.name }${ address.building ? `, ${t('common.buildingShort')} ${ address.building }` : '' }${ address.floor ? `, ${t('common.floorShort')} ${ address.floor }` : '' } ${ address.apartment ? `, ${t('common.apartmentShort')} ${ address.apartment }` : '' }`
        };
        if (address.billing) {
          acc.billing.push({
            ...parsedAddress,
            isDefault: address.defaultBilling
          });
        }
        acc.shipping.push({
          ...parsedAddress,
          isDefault: address.defaultDelivery
        });
        return acc;
      }, { shipping: [], billing: [] });

      setShippingAddresses(shipping);
      setBillingAddresses(billing);
    } else if (!addressesError) {
      setShippingAddresses([]);
      setBillingAddresses([]);
    }
  }, [addresses, addressesError]);

  useEffect(() => {
    if (billingAddresses.length) {
      const defaultAddress = billingAddresses.find(addr => addr.isDefault);
      if (defaultAddress) {
        if (!order.billingAddress) {
          order.setBillingAddress(defaultAddress.id);
        } else if (!billingAddresses.find(addr => addr.id === order.billingAddress)) {
          order.setBillingAddress(defaultAddress.id);
        }
      }
    }
  }, [
    billingAddresses,
    order.billingAddress
  ]);

  useEffect(() => {
    if (billingAddresses?.length) {
      const matchedBillingAddress = billingAddresses.find(addr => addr.id === order.billingAddress);
      if (!matchedBillingAddress) {
        setSelectedBillingAddress(billingAddresses.find(addr => addr.isDefault));
      } else {
        setSelectedBillingAddress(matchedBillingAddress);
      }
    }
  }, [
    order.billingAddress,
    billingAddresses
  ]);

  useEffect(() => {
    if (shippingAddresses?.length) {
      const defaultAddress = shippingAddresses.find(addr => addr.isDefault);
      const matchedShippingAddress = shippingAddresses.find(addr => addr.id === order.deliveryAddress);

      if (!order.deliveryAddress) {
        order.setDeliveryAddress(defaultAddress?.id);
      } else if (!matchedShippingAddress) {
        order.setDeliveryAddress(defaultAddress?.id);
        setSelectedShippingAddress(defaultAddress);
      } else {
        setSelectedShippingAddress(matchedShippingAddress);
      }
    }
  }, [
    shippingAddresses,
    order.deliveryAddress,
  ]);

  const handleOnSetAddress = type => ({ selectedItem }) => {
    if (type === 'shipping') {
      order.setDeliveryAddress(selectedItem?.id || null);
    } else {
      order.setBillingAddress(selectedItem?.id || null);
    }
  };

  const handleOnSetOption = type => selectedItemId => {
    if (selectedItemId.preventDefault) {
      // eslint-disable-next-line no-param-reassign
      selectedItemId = selectedItemId.target.id;
    }
    if (type === 'payment') {
      order.setPaymentOption(selectedItemId);
    } else {
      order.setTransportOption(selectedItemId);
    }
  };

  const handleOnChangeNote = evt => {
    order.setOrderNotes(evt.target.value || '');
  };

  const getInvalidFields = () => {
    let invalidFields;
    if (order?.dropShipment) {
      invalidFields = Object.keys(dsFormValidation).filter(key => !(dsFormValidation[key]?.isValid || dsFormValidation[key]?.isSelected));
    } else {
      invalidFields = Object.keys(formValidation).filter(key => !(formValidation[key]?.isValid || formValidation[key]?.isSelected));
    }

    return invalidFields;
  };

  const touchFields = fields => {
    const handleTouched = obj => {
      const newObj = { ...obj };
      fields.forEach(field => {
        if (newObj[field]?.isTouched === false) {
          newObj[field].isTouched = true;
        }
      });
      return newObj;
    };

    if (order?.dropShipment) {
      setDsFormValidation(handleTouched);
    } else {
      setFormValidation(handleTouched);
    }
  };

  const updateValidation = (fieldToUpdate, updates) => setFormValidation(obj => ({
    ...obj,
    [fieldToUpdate]: { ...obj[fieldToUpdate], ...updates }
  }));

  const updateDsValidation = (fieldToUpdate, updates) => setDsFormValidation(obj => ({
    ...obj,
    [fieldToUpdate]: { ...obj[fieldToUpdate], ...updates }
  }));

  const handleOnChangeName = value => {
    if (!value?.length) {
      updateDsValidation('name', { isValid: false, error: t('errors.fieldBlank') });
    } else if (value?.length < 3) {
      updateDsValidation('name', { isValid: false, error: t('errors.fieldMinLength', { minLength: 3 }) });
    } else if (value?.length > 90) {
      updateDsValidation('name', { isValid: false, error: t('errors.fieldMaxLength', { maxLength: 90 }) });
    } else {
      updateDsValidation('name', { isValid: true, error: '' });
    }
    order.setDeliveryAddressData('contactPersonName', value);
  };

  const handleOnChangeAddress = value => {
    if (!value?.length) {
      updateDsValidation('address', { isValid: false, error: t('errors.fieldBlank') });
    } else if (value?.length < 10) {
      updateDsValidation('address', { isValid: false, error: t('errors.fieldMinLength', { minLength: 10 }) });
    } else if (value?.length > 120) {
      updateDsValidation('address', { isValid: false, error: t('errors.fieldMaxLength', { maxLength: 120 }) });
    } else {
      updateDsValidation('address', { isValid: true, error: '' });
    }
    order.setDeliveryAddressData('address', value);
  };

  const handleOnChangePhone = value => {
    if (!value?.length) {
      updateDsValidation('phone', { isValid: false, error: t('errors.fieldBlank') });
    } else if (!value?.match(COUNTRIES[selectedDSCountry]?.phoneNumberRegex)) {
      updateDsValidation('phone', { isValid: false, error: t('errors.filedInvalidFormat') });
    } else {
      updateDsValidation('phone', { isValid: true, error: '' });
    }
    order.setDeliveryAddressData('contactPersonPhone', value);
  };

  const handleOnChangeRecipientEmail = value => {
    if (!(order.deliveryAddressData.deliveryType === 'emktp') && !value?.length) {
      updateDsValidation('recipientEmail', { isValid: false, error: t('errors.fieldBlank') });
    } else if (!(order.deliveryAddressData.deliveryType === 'emktp') && !value?.match(EMAIL_REGEX)) {
      updateDsValidation('recipientEmail', { isValid: false, error: t('errors.invalidEmail') });
    } else {
      updateDsValidation('recipientEmail', { isValid: true, error: '' });
    }
    order.setDeliveryAddressData('contactPersonRecipientEmail', value);
  };

  const handleOnChangePostalCode = value => {
    const { city, county } = order.deliveryAddressData;
    let lastTwoDigitsCityValue;
    let firstTwoDigitsZipCode;

    if (value && county && county === 44 && city) {
      // Extract the last 2 digits from cityValue
      lastTwoDigitsCityValue = `0${city.slice(-1)}`;

      // Extract the first 2 digits from zipCode
      firstTwoDigitsZipCode = String(value).slice(0, 2);
    }

    if (!value?.length && COUNTRIES[selectedDSCountry]?.zipCodeRequired) {
      updateDsValidation('postalCode', { isValid: false, error: t('errors.fieldBlank') });
    } else if (value && value.length === 6 && county && county === 44 && city && !(lastTwoDigitsCityValue === firstTwoDigitsZipCode)) {
      updateDsValidation('postalCode', { isValid: false, isTouched: true, error: t('errors.postalCodeDistrict', { district: lastTwoDigitsCityValue }) });
    } else if (value && !value?.match(COUNTRIES[selectedDSCountry]?.zipCodeRegex)) {
      updateDsValidation('postalCode', { isValid: false, isTouched: true, error: t('errors.filedInvalidFormat') });
    } else {
      updateDsValidation('postalCode', { isValid: true, error: '' });
    }
    order.setDeliveryAddressData('postalCode', value);
  };

  const handleOnChangeInternalRefCode = value => {
    if (order.deliveryAddressData.deliveryType === 'emktp' && !value?.length) {
      updateDsValidation('internalRefCode', { isValid: false, error: t('errors.fieldBlank') });
    } else if (order.deliveryAddressData.deliveryType === 'emktp' && !value?.match(/^[0-9]{9,11}$/)) {
      updateDsValidation('internalRefCode', { isValid: false, error: t('errors.filedInvalidFormat') });
    } else if (order.deliveryAddressData.deliveryType === 'emktp' && value.startsWith('0')) {
      updateDsValidation('internalRefCode', { isValid: false, error: t('errors.leadingZero') });
    } else {
      updateDsValidation('internalRefCode', { isValid: true, error: '' });
    }
    order.setClientOrderIdRef(value);
  };

  const handleOnChangeBillingDetails = ({ dropShipment, value }) => {
    const MIN_LENGTH = 3;
    const MAX_LENGTH = 60;

    const updateFn = dropShipment ? updateDsValidation : updateValidation;

    let validation = { isValid: true, isTouched: false, error: '' };

    if (value && value.trim().length > 0) {
      const { length } = value.trim();

      if (length < MIN_LENGTH) {
        validation = {
          isValid: false,
          error: t('errors.fieldMinLength', { minLength: MIN_LENGTH })
        };
      } else if (length > MAX_LENGTH) {
        validation = {
          isValid: false,
          error: t('errors.fieldMaxLength', { maxLength: MAX_LENGTH })
        };
      }
    }

    updateFn('billingDetails', validation);
    order.setBillingDetails(value);
  };

  const handleOnChangeDeliveryType = value => {
    if (value === 'emktp') {
      if (order.deliveryAddressData.deliveryType && order.deliveryAddressData.deliveryType !== value) {
        resetDSForm();
      }
      handleOnChangeInternalRefCode(order.clientOrderIdRef);
      updateDsValidation('contactPersonRecipientEmail', { isValid: true, error: '' });
    } else if (value === 'internalCourier' || value === 'externalCourier') {
      if (order.deliveryAddressData?.deliveryType === 'emktp' && order.deliveryAddressData?.deliveryMode === 'address') {
        resetDSForm();
      }
      updateDsValidation('internalRefCode', { isValid: true, error: '' });
      handleOnChangeRecipientEmail(order.deliveryAddressData?.contactPersonRecipientEmail);
    }

    if (
      (order.deliveryAddressData?.deliveryType === 'internalCourier' && !hasInternalDropShipmentSamedayLockerTransport && order.deliveryAddressData?.deliveryMode === 'locker')
      || (order.deliveryAddressData?.deliveryType === 'externalCourier' && !hasExternalDropShipmentSamedayLockerTransport && order.deliveryAddressData?.deliveryMode === 'locker')
    ) {
      resetDSForm();
      order.setDeliveryAddressData('deliveryMode', null);
      updateDsValidation('deliveryMode', { isSelected: false });
    }

    order.setDeliveryAddressData('deliveryType', value);
    updateDsValidation('deliveryType', { isSelected: !!value });
  };

  const handleOnChangeDeliveryMode = value => {
    if (order.deliveryAddressData?.deliveryMode && order.deliveryAddressData?.deliveryMode !== value) {
      resetDSForm();
    }
    order.setDeliveryAddressData('deliveryMode', value);
    updateDsValidation('deliveryMode', { isSelected: !!value });
  };

  const handleOnDeliveryLockerCode = value => {
    if (
      order.deliveryAddressData.deliveryType === 'emktp'
      && order.deliveryAddressData.deliveryMode === 'locker'
      && !value?.length
    ) {
      updateDsValidation('deliveryLockerCode', { isValid: false, error: t('errors.fieldBlank') });
    } else if (
      order.deliveryAddressData.deliveryType === 'emktp'
      && order.deliveryAddressData.deliveryMode === 'locker'
      && !value?.match(/^[0-9]{4,6}$/)
    ) {
      updateDsValidation('deliveryLockerCode', { isValid: false, error: t('errors.filedInvalidFormat') });
    } else {
      updateDsValidation('deliveryLockerCode', { isValid: true, error: '' });
    }
    order.setDeliveryAddressData('deliveryLockerCode', value);
  };

  useEffect(() => {
    if (!canPlaceEmagOrder && order.deliveryAddressData.deliveryType === 'emktp') {
      order.setDeliveryAddressData('deliveryType', 'internalCourier');
    }
  }, [canPlaceEmagOrder]);

  useEffect(() => {
    if (order?.dropShipment) {
      handleOnChangeDeliveryType(order.deliveryAddressData?.deliveryType);
      handleOnChangeDeliveryMode(order.deliveryAddressData?.deliveryMode);
      handleOnChangeName(order.deliveryAddressData?.contactPersonName);
      handleOnChangeAddress(order.deliveryAddressData?.address);
      handleOnChangePhone(order.deliveryAddressData?.contactPersonPhone);
      handleOnChangePostalCode(order.deliveryAddressData?.postalCode);
      handleOnChangeRecipientEmail(order.deliveryAddressData?.contactPersonRecipientEmail);
      handleOnChangeInternalRefCode(order.clientOrderIdRef);
      handleOnChangeBillingDetails({ dropShipment: true, value: order.billingDetails });

      setDsFormValidation(obj => ({
        ...obj,
        country: { ...obj.country, isSelected: !!(countries || []).find(country => country.id === order.deliveryAddressData?.country) },
        county: { ...obj.county, isSelected: !!(counties || []).find(county => county.id === order.deliveryAddressData?.county) },
        city: { ...obj.city, isSelected: !!(cities || []).find(city => city.name === order.deliveryAddressData?.city) },
        deliveryOption: {
          ...obj.deliveryOption,
          isSelected: !!order.transportOption && !!(orderOptions?.transportOptions || []).find(transportOption => transportOption.code !== 'WAREHOUSE' && transportOption.id === order.transportOption) },
        billingAddress: { ...obj.billingAddress, isValid: !!(selectedBillingAddress || (billingAddresses || []).find(addr => addr.isDefault)) },
        paymentOption: { ...obj.paymentOption, isSelected: !!order.paymentOption }
      }));
    } else {
      handleOnChangeBillingDetails({ dropShipment: false, value: order.billingDetails });

      setFormValidation(obj => ({
        ...obj,
        deliveryAddress: { ...obj.deliveryAddress, isValid: !!(selectedShippingAddress || (shippingAddresses || []).find(addr => addr.isDefault)) },
        deliveryOption: { ...obj.deliveryOption, isSelected: !!order.transportOption },
        billingAddress: { ...obj.billingAddress, isValid: !!(selectedBillingAddress || (billingAddresses || []).find(addr => addr.isDefault)) },
        paymentOption: { ...obj.paymentOption, isSelected: !!order.paymentOption }
      }));
    }
  }, [
    order?.dropShipment,
    order?.transportOption,
    shippingAddresses,
    billingAddresses,
    countries,
    counties,
    cities,
  ]);

  useEffect(() => {
    const invalidFields = getInvalidFields();
    const elementsToRemove = [];
    if (!(order.deliveryAddressData?.deliveryType !== 'emktp' && order.deliveryAddressData?.deliveryMode === 'locker')) {
      elementsToRemove.push('locker');
    }
    if (!(order.deliveryAddressData?.deliveryType === 'emktp' && order.deliveryAddressData?.deliveryMode === 'locker')) {
      elementsToRemove.push('deliveryLockerCode');
    }
    const filtered = invalidFields.filter(item => !elementsToRemove.includes(item));
    if (setIsFormValid) { // TODO: setIsFormValid is undefined on placing order
      setIsFormValid(!filtered.length);
    }
  }, [formValidation, dsFormValidation]);

  useEffect(() => {
    if (shouldShowErrors) {
      const invalidFields = getInvalidFields();
      const elementsToRemove = [];
      if (!(order.deliveryAddressData?.deliveryType !== 'emktp' && order.deliveryAddressData?.deliveryMode === 'locker')) {
        elementsToRemove.push('locker');
      }
      if (!(order.deliveryAddressData?.deliveryType === 'emktp' && order.deliveryAddressData?.deliveryMode === 'locker')) {
        elementsToRemove.push('deliveryLockerCode');
      }
      const filtered = invalidFields.filter(item => !elementsToRemove.includes(item));
      touchFields(filtered);
    }
  }, [shouldShowErrors]);

  useEffect(() => {
    handleOnChangePostalCode(order.deliveryAddressData?.postalCode);
  }, [selectedDSCountry]);

  const handleSetLocation = type => ({ selectedItem }) => {
    const toLocker = order.deliveryAddressData?.deliveryType !== 'emktp' && order.deliveryAddressData?.deliveryMode === 'locker';
    if (type === 'locker') {
      handleOnSetLocker({ selectedItem });
    }

    if (type === 'city') {
      order.setDeliveryAddressData('city', selectedItem?.name);
      setLockers(
        selectedItem?.id
          ? cache.get(`${ API_LOCKERS }?urgent_cargus_city_id=${ selectedItem?.id }`) || []
          : null
      );
      handleOnSetLocker({ selectedItem: null });
    }

    if (type === 'county') {
      order.setDeliveryAddressData('county', selectedItem?.id);
      setCities(
        selectedItem?.id
          ? cache.get(`${ API_COUNTIES }/${ selectedItem.id }/cities${ toLocker ? '?easybox=1' : '' }`) || []
          : null
      );
      order.setDeliveryAddressData('city', null);
      handleOnSetLocker({ selectedItem: null });
    }

    if (type === 'country') {
      order.setDeliveryAddressData('country', selectedItem?.id);
      setCounties(
        selectedItem?.id
          ? cache.get(`${ API_COUNTRIES }/${ selectedItem.id }/${ COUNTIES }${ toLocker ? '?easybox=1' : '' }`) || []
          : null
      );
      order.setDeliveryAddressData('county', null);
      order.setDeliveryAddressData('city', null);
      handleOnSetLocker({ selectedItem: null });
    }
  };

  const handleOnSetLocker = ({ selectedItem }) => {
    const isLockerDelivery = order.deliveryAddressData?.deliveryType !== 'emktp' && order.deliveryAddressData?.deliveryMode === 'locker';

    updateDsValidation('locker', { isValid: !!selectedItem });
    if (selectedItem) {
      order.setDeliveryAddressData('deliveryLockerId', selectedItem?.value);
      handleOnChangeAddress(selectedItem?.address);
      handleOnChangePostalCode(selectedItem?.zipCode);
    } else {
      order.setDeliveryAddressData('deliveryLockerId', null);
      if (isLockerDelivery) {
        handleOnChangeAddress('');
        handleOnChangePostalCode('');
      }
    }
  };

  const resetDSForm = () => {
    handleSetLocation('country')({ selectedItem: null });
    setLockers(null);
    handleOnSetLocker({ selectedItem: null });
    handleOnChangeName(null);
    handleOnChangeAddress(null);
    handleOnChangePhone(null);
    handleOnChangePostalCode(null);
    handleOnChangeRecipientEmail(null);
    handleOnChangeInternalRefCode(null);
    handleOnDeliveryLockerCode(null);
  };

  return (
    <OrderInfoComponent
      order={ order }
      editable={ editable }
      countries={ countries }
      counties={ counties }
      cities={ cities }
      hasInternalDropShipmentSamedayLockerTransport={ hasInternalDropShipmentSamedayLockerTransport }
      hasExternalDropShipmentSamedayLockerTransport={ hasExternalDropShipmentSamedayLockerTransport }
      setCountry={ handleSetLocation('country') }
      setCounty={ handleSetLocation('county') }
      setCity={ handleSetLocation('city') }
      setLocker={ handleSetLocation('locker') }
      lockers={ lockers }
      loadingAddresses={ !addresses || loadingAddresses }
      staticBlocks={ staticBlocks }
      canPlaceEmagOrder={ canPlaceEmagOrder }
      canPlaceExternalOrder={ canPlaceExternalOrder }
      canPlaceInternalOrder={ canPlaceInternalOrder }
      billingAddresses={ billingAddresses || [] }
      selectedBillingAddress={ selectedBillingAddress }
      shippingAddresses={ shippingAddresses || [] }
      selectedShippingAddress={ selectedShippingAddress }
      setShipping={ handleOnSetAddress('shipping') }
      setBilling={ handleOnSetAddress('billing') }
      paymentOptions={ orderOptions?.paymentOptions || [] }
      transportOptions={ orderOptions?.transportOptions || [] }
      loadingOptions={ !orderOptions || loadingOptions }
      loadingBlocks={ !staticBlocks || loadingBlocks }
      setPaymentOption={ handleOnSetOption('payment') }
      setTransportOption={ handleOnSetOption('transport') }
      onChangeNote={ handleOnChangeNote }
      selectedDSCountry={ selectedDSCountry }
      setSelectedDSCountry={ setSelectedDSCountry }
      canSelectedDSCountryPlaceEmagOrder={ canPlaceEmagOrder && (emagDSCountries && !!emagDSCountries.find(c => c.id === selectedDSCountry)) }
      formValidation={ formValidation }
      updateValidation={ updateValidation }
      dsFormValidation={ dsFormValidation }
      updateDsValidation={ updateDsValidation }
      handleOnChangeName={ handleOnChangeName }
      handleOnChangeAddress={ handleOnChangeAddress }
      handleOnChangePhone={ handleOnChangePhone }
      handleOnChangePostalCode={ handleOnChangePostalCode }
      handleOnChangeInternalRefCode={ handleOnChangeInternalRefCode }
      handleOnDeliveryLockerCode={ handleOnDeliveryLockerCode }
      handleOnChangeDeliveryType={ handleOnChangeDeliveryType }
      handleOnChangeDeliveryMode={ handleOnChangeDeliveryMode }
      handleOnChangeRecipientEmail={ handleOnChangeRecipientEmail }
      handleOnChangeBillingDetails={ handleOnChangeBillingDetails }
      mappedDeliveryTypes={ mappedDeliveryTypes }
    />
  );
});

OrderInfo.propTypes = {
  order: object,
  editable: bool,
  selectedDSCountry: number,
  setSelectedDSCountry: func,
  setIsFormValid: func,
  shouldShowErrors: bool,
};

OrderInfo.displayName = 'OrderInfoContainer';

export default OrderInfo;
